import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import AchBankFields, {
  achBankReady,
  type AchAccountType,
} from '../../../Components/AchBankFields';
import {
  completePortalPayflowSession,
  createPortalPayflowSession,
  getPortalPayflowSession,
  payPortalACH,
  previewPortalPayment,
} from '../../../endpoints/portalLoans';
import type {
  PortalPayConfirmState,
  PortalPaymentPreviewResponse,
  PortalPayflowSessionResponse,
} from '../../../types/portalLoans';
import { getApiErrorMessage } from '../../../utils/httpClientTs';
import PayflowHostedCardFrame, {
  payflowDeclineMessage,
  type PayflowHostedMessage,
} from '../../../Components/PayflowHostedCardFrame';
import { formatPortalCurrency } from '../mock/store';
import { ELF_TEAL, elfPrimaryButtonSx } from '../elfTheme';

const fmtMoney = (n: number) => formatPortalCurrency(n);

const LoanPayConfirm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const draft = (location.state || null) as PortalPayConfirmState | null;

  const [preview, setPreview] = useState<PortalPaymentPreviewResponse | null>(
    null
  );
  const [previewPending, setPreviewPending] = useState(false);
  const [session, setSession] = useState<PortalPayflowSessionResponse | null>(
    null
  );
  const [sessionPending, setSessionPending] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [waitingConfirm, setWaitingConfirm] = useState(false);
  const finalizeInFlightRef = useRef(false);
  const waitStartedRef = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [paidByBank, setPaidByBank] = useState(false);
  const [receiptAmount, setReceiptAmount] = useState(0);
  const [payMethod, setPayMethod] = useState<'card' | 'bank'>('card');
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountType, setAccountType] = useState<AchAccountType>('checking');

  const draftAmount = draft?.amount;

  useEffect(() => {
    if (draftAmount == null || draftAmount <= 0) return;
    let cancelled = false;
    setPreviewPending(true);
    setError(null);
    previewPortalPayment(draftAmount)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setPreview(null);
          setError(getApiErrorMessage(err) || 'Unable to preview payment.');
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewPending(false);
      });
    return () => {
      cancelled = true;
    };
  }, [draftAmount]);

  const bankReady = achBankReady(routingNumber, accountNumber, accountType);

  const handlePayBank = async () => {
    if (!draft || draft.amount <= 0 || !bankReady) return;
    setError(null);
    setCompleting(true);
    try {
      await payPortalACH({
        amount: draft.amount,
        routing_number: routingNumber.trim(),
        account_number: accountNumber.trim(),
        account_type: accountType,
      });
      setReceiptAmount(draft.amount);
      setPaidByBank(true);
      setDone(true);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['portal-loans-me'] }),
        queryClient.invalidateQueries({ queryKey: ['portal-loans-history'] }),
      ]);
    } catch (err) {
      setError(getApiErrorMessage(err) || 'Unable to complete payment.');
    } finally {
      setCompleting(false);
    }
  };

  const handleStartCard = async () => {
    if (!draft || draft.amount <= 0) return;
    setError(null);
    setSession(null);
    setSessionPending(true);
    try {
      const data = await createPortalPayflowSession(draft.amount);
      waitStartedRef.current = Date.now();
      setWaitingConfirm(false);
      setSession(data);
    } catch (err) {
      setError(getApiErrorMessage(err) || 'Unable to start card checkout.');
    } finally {
      setSessionPending(false);
    }
  };

  const finalize = useCallback(async () => {
    if (!session || finalizeInFlightRef.current) return;
    finalizeInFlightRef.current = true;
    setCompleting(true);
    setError(null);
    try {
      await completePortalPayflowSession(session.session_id);
      setReceiptAmount(draft?.amount ?? 0);
      setDone(true);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['portal-loans-me'] }),
        queryClient.invalidateQueries({ queryKey: ['portal-loans-history'] }),
      ]);
    } catch (err) {
      setError(
        getApiErrorMessage(err) ||
          'Waiting for PayPal to confirm this payment. Do not enter your card again.'
      );
    } finally {
      setCompleting(false);
      finalizeInFlightRef.current = false;
    }
  }, [session, draft?.amount, queryClient]);

  useEffect(() => {
    if (!session || done) return;
    let stopped = false;
    const tick = async () => {
      if (stopped || finalizeInFlightRef.current) return;
      if (
        waitStartedRef.current &&
        Date.now() - waitStartedRef.current > 45000
      ) {
        setWaitingConfirm(true);
      }
      try {
        const st = await getPortalPayflowSession(session.session_id);
        if (stopped) return;
        if (st.status === 'completed' || st.payment_id) {
          setReceiptAmount(draft?.amount ?? 0);
          setDone(true);
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['portal-loans-me'] }),
            queryClient.invalidateQueries({
              queryKey: ['portal-loans-history'],
            }),
          ]);
          return;
        }
        if (
          st.status === 'declined' ||
          st.status === 'failed' ||
          st.status === 'expired'
        ) {
          setError(payflowDeclineMessage(st.respmsg, st.result));
          setSession(null);
          return;
        }
        if (st.status === 'approved' || st.has_pnref) {
          void finalize();
        }
      } catch {
        // Keep polling until PayPal confirms.
      }
    };
    const id = window.setInterval(() => void tick(), 2000);
    void tick();
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, [session, done, draft?.amount, queryClient, finalize]);

  const onPayflowMessage = useCallback(
    (msg: PayflowHostedMessage) => {
      if (msg.status === 'approved') {
        void finalize();
        return;
      }
      if (msg.status === 'canceled') {
        setError('Card entry was canceled.');
        setSession(null);
        return;
      }
      setError(payflowDeclineMessage(msg.respmsg, msg.result));
      setSession(null);
    },
    [finalize]
  );

  if (!draft && !done) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          No payment details found. Start from Make a payment.
        </Alert>
        <Button
          component={RouterLink}
          to="/portal/loans/pay"
          variant="contained"
        >
          Make a payment
        </Button>
      </Box>
    );
  }

  if (done) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Payment submitted
        </Typography>
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            {paidByBank
              ? 'Your bank payment was applied to your ELF account.'
              : 'Your card payment was applied to your ELF account.'}
          </Alert>
          <Typography variant="h5" gutterBottom>
            {fmtMoney(receiptAmount)}
          </Typography>
          <Typography color="text.secondary" paragraph>
            You can review the posting on your payment history. A confirmation
            email is not sent yet.
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              component={RouterLink}
              to="/portal/loans/history"
              variant="contained"
              sx={elfPrimaryButtonSx}
            >
              View history
            </Button>
            <Button component={RouterLink} to="/portal/loans">
              Back to loan home
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Confirm payment
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Review the allocation, then pay by card or bank account.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Amount
        </Typography>
        <Typography variant="h4" sx={{ mb: 2 }}>
          {fmtMoney(draft!.amount)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Method
        </Typography>
        {!session && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={payMethod}
            onChange={(_, value) => value && setPayMethod(value)}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="card">Card</ToggleButton>
            <ToggleButton value="bank">Bank account</ToggleButton>
          </ToggleButtonGroup>
        )}
        {session && <Typography sx={{ mb: 2 }}>Debit / credit card</Typography>}
        <Typography variant="body2" color="text.secondary">
          Type
        </Typography>
        <Typography sx={{ mb: 2 }}>
          {draft!.amountMode === 'installment'
            ? 'Scheduled installment'
            : 'Custom amount'}
        </Typography>

        {previewPending && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={20} sx={{ color: ELF_TEAL }} />
            <Typography variant="body2">Loading allocation preview…</Typography>
          </Box>
        )}

        {preview && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              How this payment will apply
            </Typography>
            <Typography variant="body2">
              Charges {fmtMoney(preview.total_applied_to_charges)} · Interest{' '}
              {fmtMoney(preview.total_applied_to_interest)} · Principal{' '}
              {fmtMoney(preview.total_applied_to_principal)}
            </Typography>
            {preview.unapplied_amount > 0.004 && (
              <Typography variant="body2" color="text.secondary">
                Unapplied {fmtMoney(preview.unapplied_amount)}
              </Typography>
            )}
            {preview.warnings?.map((w) => (
              <Alert key={w} severity="warning" sx={{ mt: 1 }}>
                {w}
              </Alert>
            ))}
          </Box>
        )}

        {!session && payMethod === 'bank' && (
          <Box sx={{ mb: 2 }}>
            <AchBankFields
              routingNumber={routingNumber}
              accountNumber={accountNumber}
              accountType={accountType}
              onRoutingNumber={setRoutingNumber}
              onAccountNumber={setAccountNumber}
              onAccountType={setAccountType}
              disabled={completing}
            />
          </Box>
        )}

        {!session && (
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={() =>
                void (payMethod === 'bank'
                  ? handlePayBank()
                  : handleStartCard())
              }
              disabled={
                previewPending ||
                !preview ||
                sessionPending ||
                completing ||
                (payMethod === 'bank' && !bankReady)
              }
              sx={elfPrimaryButtonSx}
            >
              {payMethod === 'bank'
                ? completing
                  ? 'Paying…'
                  : 'Pay from bank account'
                : sessionPending
                  ? 'Starting checkout…'
                  : 'Enter card details'}
            </Button>
            <Button
              onClick={() => navigate('/portal/loans/pay')}
              disabled={sessionPending || completing}
            >
              Back
            </Button>
          </Stack>
        )}

        {session && (
          <Box sx={{ mt: 2 }}>
            {completing && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Finalizing your payment…
              </Alert>
            )}
            {waitingConfirm && !completing && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Still waiting for PayPal confirmation. Do not enter your card
                again.
              </Alert>
            )}
            <Alert severity="warning" sx={{ mb: 2 }}>
              If PayPal showed a receipt, stay on this page. Starting over can
              charge you twice.
            </Alert>
            <PayflowHostedCardFrame
              iframeUrl={session.iframe_url}
              sessionId={session.session_id}
              onMessage={onPayflowMessage}
            />
            <Button
              sx={{ mt: 2 }}
              onClick={() => {
                setError(
                  'Go back only if you have not submitted the card. Starting over can charge you twice.'
                );
                setSession(null);
              }}
              disabled={completing}
            >
              Cancel card entry
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default LoanPayConfirm;
